/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, lazy, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ReactTagManager } from 'react-gtm-ts';
import { useTranslation } from 'react-i18next';
import { httpClient } from 'api';
import { Toast } from 'primereact/toast';
import { PageMissing } from 'components/PageMissing';
import { Button } from 'components/_new/Button';
import { MaintenanceNotification } from 'components/MaintenanceNotification';
import { useMaintenance } from 'hooks/useMaintenance';

// contexts
import { GlobalContextProvider } from 'contexts/globalContext';
import { AuthContextProvider } from 'contexts/authContext';
import { PanelContextProvider } from 'contexts/panelContext';

// auth
import { Verify } from 'components/auth/Verify';
import AuthContainer from 'components/auth/AuthContainer';
import Register from 'components/auth/Register';
import PasswordSetup from 'components/auth/PasswordSetup';
import Login from 'components/auth/Login';
import PasswordReset from 'components/auth/PasswordReset';
import EmailChangeConfirm from 'components/auth/EmailChangeConfirm';
import RegisterOrganization from 'components/auth/RegisterOrganization';
import Confirm from 'components/auth/Confirm';
import ExternalAuth from 'components/auth/ExternalAuth';

import RequireAuth from 'components/RequireAuth';
import ChangeOrganizationWrapper from 'components/_common/ChangeOrganizationWrapper';
import RequireTerms from 'components/auth/RequireTerms';
import { ScopeContextProvider } from 'contexts/scopeContext';

// landing page

import { Enter } from 'components/Enter';

// user panel
import UserPanelLayout from 'components/userPanel/UserPanelLayout';
import Wall from 'components/wall/Wall';

// import FilesContainer from 'components/knowledgeDatabase/FilesContainer';
import LinkedInBadgePage from 'components/LinkedInBadgePage';

// program/admin panel

import { OrganizationGuard } from 'components/organizationPanel/OrganizationGuard';

import { PairProvider } from 'contexts/pairContext';

// other

import { PageLoading } from 'components/PageLoading';
import i18n from 'locale';
import { useWindowSize } from 'hooks/useWindowSize';

import { ScopeChatContainer } from 'components/chat/ChatContainer';
import { InstallPromptProvider } from 'components/_new/PwaManager/InstallPromptContext';

import { SessionTypeEnum } from 'types/Session';

const OrganizationLandingPage = lazy(() => import('components/landingPages/OrganizationLandingPage'));
const ProgramLandingPage = lazy(() => import('components/landingPages/ProgramLandingPage'));
const Application = lazy(() => import('components/userPanel/Application'));
const Sessions = lazy(() => import('components/userPanel/sessions/Sessions'));
const SessionsList = lazy(() => import('components/userPanel/sessions/SessionsList'));
const SessionAgenda = lazy(() => import('components/userPanel/sessions/SessionAgenda'));
const Contract = lazy(() =>
	import('components/userPanel/contract/Contract').then((module) => ({ default: module.Contract }))
);
const FinalReport = lazy(() =>
	import('components/userPanel/finalReport/FinalReport').then((module) => ({ default: module.FinalReport }))
);
const ProcessSummary = lazy(() => import('components/userPanel/processSummary/ProcessSummary'));
const ProcessRoadmap = lazy(() =>
	import('components/userPanel/processRoadmap/ProcessRoadmap').then((module) => ({ default: module.ProcessRoadmap }))
);
const MentibotAI = lazy(() =>
	import('components/userPanel/mentibotAi/MentibotAI').then((module) => ({ default: module.MentibotAI }))
);
const MentibotAIList = lazy(() =>
	import('components/userPanel/mentibotAi/MentibotAIList').then((module) => ({ default: module.MentibotAIList }))
);
const MentibotAIForm = lazy(() =>
	import('components/userPanel/mentibotAi/MentibotAIForm').then((module) => ({ default: module.MentibotAIForm }))
);
const Browse = lazy(() =>
	import('components/userPanel/userFiles/Browse').then((module) => ({ default: module.Browse }))
);
const DispalyPage = lazy(() =>
	import('components/userPanel/userFiles/DisplayPage').then((module) => ({ default: module.DispalyPage }))
);
const Contact = lazy(() => import('components/userPanel/Contact').then((module) => ({ default: module.Contact })));
const GoalsWrapper = lazy(() => import('components/userPanel/goals/Goals'));
const OpenSurveyRedirect = lazy(() => import('components/userPanel/OpenSurveyRedirect'));
const OrganizationPanel = lazy(() => import('components/organizationPanel/OrganizationPanel'));
const OrganizationList = lazy(() =>
	import('components/organizationPanel/OrganizationList').then((module) => ({ default: module.OrganizationList }))
);
const OrganizationProgramList = lazy(() => import('components/organizationPanel/OrganizationProgramList'));
const ProgramPanel = lazy(() => import('components/programPanel/ProgramPanel'));
const ProgramDashboard = lazy(() => import('components/programPanel/programDashboard/dashboard/ProgramDashboard'));
const ProgramPairList = lazy(() => import('components/programPanel/ProgramPairList'));
const ProgramPairDetails = lazy(() => import('components/programPanel/ProgramPairDetails'));
const ProgramApplicationList = lazy(() => import('components/programPanel/ProgramApplicationList'));
const ProgramPairingDetails = lazy(() => import('components/programPanel/ProgramApplicationDetails'));
const ProgramMembershipList = lazy(() => import('components/programPanel/ProgramMembershipList'));
const ProgramMembership = lazy(() => import('components/programPanel/ProgramMembership'));
const ProgramPostList = lazy(() =>
	import('components/programPanel/ProgramPostList').then((module) => ({ default: module.ProgramPostList }))
);
const ProgramPostAdd = lazy(() =>
	import('components/programPanel/ProgramPostAdd').then((module) => ({ default: module.ProgramPostAdd }))
);
const ProgramPostEdit = lazy(() =>
	import('components/programPanel/ProgramPostEdit').then((module) => ({ default: module.ProgramPostEdit }))
);
const MyPersonalDetails = lazy(() =>
	import('components/userPanel/MyPersonalDetails').then((module) => ({ default: module.MyPersonalDetails }))
);
const PairDetails = lazy(() =>
	import('components/userPanel/PairDetails').then((module) => ({ default: module.PairDetails }))
);
const ProgramStatisticsNew = lazy(() =>
	import('components/programPanel/programStatistics/ProgramStatisticsNew').then((module) => ({
		default: module.ProgramStatisticsNew,
	}))
);
const PairMembers = lazy(() =>
	import('components/userPanel/pairMembers/PairMembers').then((module) => ({ default: module.PairMembers }))
);
const FinalSurveyAccess = lazy(() =>
	import('components/finalSurveyPage/FinalSurveyAccess').then((module) => ({ default: module.FinalSurveyAccess }))
);
const FinalSurveyPanel = lazy(() =>
	import('components/finalSurveyPage/FinalSurveyPanel').then((module) => ({ default: module.FinalSurveyPanel }))
);
const ProgramLandingPageEditor = lazy(() =>
	import('components/programPanel/ProgramLandingPageEditor').then((module) => ({
		default: module.ProgramLandingPageEditor,
	}))
);
const ProgramConfig = lazy(() => import('components/programPanel/programDashboard/config/ProgramConfig'));
const Pairing = lazy(() => import('components/userPanel/pairing/Pairing'));
const KnowledgeBasePage = lazy(() =>
	import('components/userPanel/KnowledgeBasePage').then((module) => ({ default: module.KnowledgeBasePage }))
);
const ProgramKnowledgeBaseContainer = lazy(() =>
	import('components/programPanel/knowledgeBase/ProgramKnowledgeBase').then((module) => ({
		default: module.ProgramKnowledgeBaseContainer,
	}))
);
const ProgramPostMentibotInfo = lazy(() =>
	import('components/programPanel/ProgramPostMentibotInfo').then((module) => ({
		default: module.ProgramPostMentibotInfo,
	}))
);
const CoordinatorPanel = lazy(() =>
	import('components/coordinatorPanel/CoordinatorPanel').then((module) => ({ default: module.CoordinatorPanel }))
);
const CoordinatorPairList = lazy(() =>
	import('components/coordinatorPanel/CoordinatorPairList').then((module) => ({
		default: module.CoordinatorPairList,
	}))
);
const CoordinatorPairDetails = lazy(() =>
	import('components/coordinatorPanel/CoordinatorPairDetails').then((module) => ({
		default: module.CoordinatorPairDetails,
	}))
);
const CoordinatorApplicationDetails = lazy(() =>
	import('components/coordinatorPanel/CoordinatorApplicationDetails').then((module) => ({
		default: module.CoordinatorApplicationDetails,
	}))
);
const CoordinatorKnowledgeBaseContainer = lazy(() =>
	import('components/coordinatorPanel/CoordinatorKnowledgeBase').then((module) => ({
		default: module.CoordinatorKnowledgeBaseContainer,
	}))
);
const CoordinatorProgramDashboard = lazy(() =>
	import('components/coordinatorPanel/CoordinatorProgramDashboard').then((module) => ({
		default: module.CoordinatorProgramDashboard,
	}))
);
const OrganizationMemberships = lazy(() =>
	import('components/organizationPanel/OrganizationMemberships').then((module) => ({
		default: module.OrganizationMemberships,
	}))
);
const TimelinePage = lazy(() =>
	import('components/userPanel/TimelinePage').then((module) => ({ default: module.TimelinePage }))
);
const MentorDevelopment = lazy(() => import('components/userPanel/mentorDevelopment/MentorDevelopment'));
const ProgramTimelineEventAdd = lazy(() =>
	import('components/programPanel/timeline/ProgramTimelineEventAdd').then((module) => ({
		default: module.ProgramTimelineEventAdd,
	}))
);
const ProgramTimelineEventEdit = lazy(() =>
	import('components/programPanel/timeline/ProgramTimelineEventEdit').then((module) => ({
		default: module.ProgramTimelineEventEdit,
	}))
);
const ProgramTimelineList = lazy(() =>
	import('components/programPanel/timeline/ProgramTimelineList').then((module) => ({
		default: module.ProgramTimelineList,
	}))
);
const ProgramTimelineEventDetails = lazy(() =>
	import('components/programPanel/timeline/ProgramTimelineEventDetails').then((module) => ({
		default: module.ProgramTimelineEventDetails,
	}))
);
const ProgramPairing = lazy(() => import('components/programPanel/pairing/ProgramPairing'));
const SessionAgendaAddNote = lazy(() =>
	import('components/userPanel/sessions/SessionAgendaAddNote').then((module) => ({
		default: module.SessionAgendaAddNote,
	}))
);
const SessionNamedNumberRedirect = lazy(() =>
	import('components/userPanel/sessions/SessionNamedNumberRedirect').then((module) => ({
		default: module.SessionNamedNumberRedirect,
	}))
);
const VideoCallPage = lazy(() =>
	import('components/videocall/VideocallCockpit').then((module) => ({ default: module.VideoCallPage }))
);
const ProgramFeedback = lazy(() =>
	import('./components/programPanel/programFeedback/ProgramFeedback').then((module) => ({
		default: module.ProgramFeedback,
	}))
);
const EditApplicationQuestions = lazy(() => import('./components/programPanel/EditApplicationQuestions'));
const EditContract = lazy(() => import('./components/programPanel/EditContract'));
const ProgramFeedbackEditQuestions = lazy(() =>
	import('./components/programPanel/programFeedback/ProgramFeedbackEditQuestions').then((module) => ({
		default: module.ProgramFeedbackEditQuestions,
	}))
);

const isDevelopment = process.env.NODE_ENV === 'development';
const googleTagId = process.env.REACT_APP_GTM;
if (googleTagId) {
	ReactTagManager.init({
		code: googleTagId,
		debug: isDevelopment,
		performance: true,
	});
}

export interface RouterUrlParams<T = string> {
	[key: string]: T;
}

const namedSessionNumberRouteTypes = [
	SessionTypeEnum.FIRST,
	SessionTypeEnum.SECOND,
	SessionTypeEnum.MIDDLE,
	SessionTypeEnum.PENULTIMATE,
	SessionTypeEnum.LAST,
];

const WhiteboardPage = lazy(() =>
	import('components/whiteboard/WhiteboardPage').then(({ WhiteboardPage }) => ({ default: WhiteboardPage }))
);

const App = () => {
	const toastRef = useRef<Toast>(null);
	const { scheduledDate, showNotification } = useMaintenance();

	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					mutations: {
						retry: false,
						// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Error types
						onError: (error: any) => {
							if (error.response?.data?.message) {
								toastRef.current?.show({
									severity: 'error',
									life: 3000,
									summary: 'Error Message',
									detail: error.response.data.message,
								});
							}
						},
					},
					queries: {
						retry: false,
						enabled: false,
						refetchOnWindowFocus: false,
						// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Error types
						onError: (error: any) => {
							if (error.response?.data?.message) {
								toastRef.current?.show({
									severity: 'error',
									life: 3000,
									summary: 'Error Message',
									detail: error.response.data.message,
								});
							} else {
								console.error(error);
							}
						},
					},
				},
			})
	);

	const [apiLoading, setApiLoading] = useState(true);
	const [apiCheckDisabled, setApiCheckDisabled] = useState(false);
	const [apiError, setApiError] = useState<any>(null);
	const { t } = useTranslation();
	const apiCheck = useCallback(() => {
		if (!apiCheckDisabled) {
			setApiCheckDisabled(true);
			setTimeout(() => {
				setApiCheckDisabled(false);
			}, 2500);

			setApiError(null);
			setApiLoading(true);
			httpClient
				.get<string>('status', undefined, {
					timeout: 15000,
				})
				.then((response: string) => {
					if (response === 'ok') {
						setApiLoading(false);
					}
				})
				.catch((err: any) => {
					setApiLoading(false);
					setApiError(err);
				});
		}
	}, [apiCheckDisabled]);

	const checkOverlayScrollbars = () => {
		const outer = document.createElement('div');
		outer.style.visibility = 'hidden';
		outer.style.position = 'absolute';
		outer.style.top = '-9999px';
		outer.style.width = '100px';
		outer.style.height = '100px';
		outer.style.overflow = 'scroll';

		document.body.appendChild(outer);
		const scrollbarWidth = outer.offsetWidth - outer.clientWidth;
		outer.remove();

		const isOverlayScrollbars = scrollbarWidth === 0;
		if (isOverlayScrollbars) {
			document.body.classList.add('overlay-scrollbar');
		} else {
			document.body.classList.remove('overlay-scrollbar');
		}
	};

	useWindowSize({
		onChange: () => {
			checkOverlayScrollbars();
		},
	});

	useEffect(() => {
		apiCheck();
		checkOverlayScrollbars();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (apiError && !apiLoading) {
		return (
			<div className="h-full w-full flex flex-1 justify-content-center align-items-center">
				{!apiError && apiLoading && <PageLoading text={i18n.t('start.applicationLoading')} />}
				{apiError && !apiLoading && (
					<div className="flex flex-column justify-content-center align-items-center">
						<h2>{`${i18n.t('start.serverNotResponding')}`}</h2>
						<Button onClick={apiCheck} label={i18n.t('start.tryAgain')} disabled={apiCheckDisabled} />
					</div>
				)}
			</div>
		);
	}

	return (
		<>
			<Helmet title={t('landingPages.helmetTitle')} titleTemplate={`%s | ${t('landingPages.helmetAppName')}`}>
				<meta name="description" content={t('landingPages.helmetDescription')} />
			</Helmet>

			{scheduledDate && showNotification && <MaintenanceNotification />}

			<QueryClientProvider client={queryClient}>
				<GlobalContextProvider
					value={{
						toastRef,
					}}
				>
					<InstallPromptProvider>
						<AuthContextProvider>
							<Toast ref={toastRef as RefObject<Toast>} position="bottom-right" />
							<Suspense fallback={<PageLoading text={t('start.applicationLoading')} />}>
								<Routes>
									<Route index element={<Enter />} />
									<Route element={<AuthContainer />}>
										<Route path="verify" element={<Verify />} />
										<Route path="login" element={<Login />} />
										<Route path="register-organization" element={<RegisterOrganization />} />
										<Route path="register" element={<Register />} />
										<Route path="confirm-organization/:token" element={<Confirm />} />
										<Route path="password-reset" element={<PasswordReset />} />
										<Route path="confirm/:token" element={<PasswordSetup mode="confirm" />} />
										<Route path="reset/:token" element={<PasswordSetup mode="reset" />} />
										<Route path="email-change/:token" element={<EmailChangeConfirm />} />
									</Route>
									<Route path="auth/:type" element={<ExternalAuth />} />

									<Route element={<RequireAuth strict />}>
										<Route path="change-organization" element={<ChangeOrganizationWrapper />} />
										<Route path="require-terms" element={<RequireTerms />} />
										<Route path="meeting/:sessionMeetingUuid" element={<VideoCallPage />} />
									</Route>

									<Route element={<ScopeContextProvider />}>
										<Route element={<RequireAuth strict />}>
											<Route
												path="panel"
												element={
													<ScopeChatContainer context="panel">
														<Outlet />
													</ScopeChatContainer>
												}
											>
												<Route index element={<Navigate to="/" replace />} />
												<Route path=":organizationName">
													<Route index element={<Navigate to="/" replace />} />
													<Route path=":programName" element={<PanelContextProvider />}>
														<Route path="opensurvey" element={<OpenSurveyRedirect />} />
														{namedSessionNumberRouteTypes.map((sessionNumberName) => (
															<React.Fragment key={sessionNumberName}>
																<Route
																	path={`:type/pair/:pairId/session/${sessionNumberName}`}
																	element={
																		<SessionNamedNumberRedirect
																			sessionNumberName={sessionNumberName}
																		/>
																	}
																/>
																<Route
																	path={`:type/pair/:pairId/session/${sessionNumberName}/:subpage`}
																	element={
																		<SessionNamedNumberRedirect
																			sessionNumberName={sessionNumberName}
																		/>
																	}
																/>
															</React.Fragment>
														))}
														<Route
															path=":type/pair/:pairId/session/:sessionId/note/:userFileId"
															element={<SessionAgendaAddNote />}
														/>
														<Route
															path=":type/pair/:pairId/session/:sessionId/note"
															element={<SessionAgendaAddNote />}
														/>

														<Route path=":type" element={<UserPanelLayout />}>
															<Route index element={<Wall />} />
															<Route
																path="personal-details"
																element={<MyPersonalDetails />}
															/>
															<Route path="application" element={<Application />} />
															<Route path="pairing" element={<Pairing />} />
															<Route path="browseMembers" element={<PairMembers />} />
															<Route
																path="knowledge-base/*"
																element={<KnowledgeBasePage />}
															/>
															<Route
																path="linkedin-badge"
																element={<LinkedInBadgePage />}
															/>
															<Route
																path="mentor-development"
																element={<MentorDevelopment />}
															/>
															<Route path="pair/:pairId" element={<PairProvider />}>
																<Route
																	index
																	element={<Navigate to="contract" replace />}
																/>
																<Route path="contract" element={<Contract />} />

																<Route path="session" element={<Sessions />}>
																	<Route index element={<SessionsList />} />
																	<Route
																		path=":sessionId/*"
																		element={<SessionAgenda />}
																	/>
																</Route>

																<Route path="mentee-plan" element={<GoalsWrapper />} />
																<Route path="final-report" element={<FinalReport />} />
																<Route
																	path="final-survey"
																	element={<FinalSurveyPanel />}
																/>
																<Route
																	path="process-summary"
																	element={<ProcessSummary />}
																/>
																<Route
																	path="process-roadmap"
																	element={<ProcessRoadmap />}
																/>
																<Route
																	path="process-card"
																	element={<Navigate to="process-roadmap" replace />}
																/>

																<Route path="mentibot-ai" element={<MentibotAI />}>
																	<Route
																		index
																		element={<Navigate to="list" replace />}
																	/>
																	<Route path="list" element={<MentibotAIList />} />
																	<Route
																		path="form/questions"
																		element={
																			<MentibotAIForm formType="questions" />
																		}
																	/>
																	<Route
																		path="form/techniques"
																		element={
																			<MentibotAIForm formType="techniques" />
																		}
																	/>
																	<Route
																		path="form/smart"
																		element={<MentibotAIForm formType="smart" />}
																	/>
																	<Route
																		path="form/homework"
																		element={<MentibotAIForm formType="homework" />}
																	/>
																</Route>
																<Route path="userfiles">
																	<Route index element={<Browse />} />
																	<Route
																		path="display/:userFileId"
																		element={<DispalyPage />}
																	/>
																</Route>
																<Route path="pair-details" element={<PairDetails />} />
															</Route>
															<Route path="contact" element={<Contact />} />

															<Route path="timeline" element={<TimelinePage />} />
														</Route>
													</Route>
												</Route>
											</Route>
											<Route path="organization/*" element={<OrganizationPanel />}>
												<Route index element={<Navigate to="list" replace />} />
												<Route path="list" element={<OrganizationList />} />
												<Route path=":organizationName/*" element={<OrganizationGuard />}>
													<Route index element={<Navigate to="program" replace />} />
													<Route path="memberships" element={<OrganizationMemberships />} />
													<Route path="program/*">
														<Route index element={<Navigate to="list" replace />} />
														<Route path="list" element={<OrganizationProgramList />} />
														<Route path="edit/:id" />
													</Route>
												</Route>
											</Route>
											<Route
												path="program/:organizationName/:programName/*"
												element={
													<ScopeChatContainer context="program-admin">
														<ProgramPanel />
													</ScopeChatContainer>
												}
											>
												<Route index element={<Navigate to="dashboard" replace />} />
												<Route path="dashboard" element={<ProgramDashboard />} />
												<Route path="pairing" element={<ProgramPairing />} />
												<Route path="config" element={<ProgramConfig />} />
												<Route path="pair/*">
													<Route index element={<Navigate to="list" replace />} />
													<Route path="list" element={<ProgramPairList />} />
													<Route path=":pairId" element={<ProgramPairDetails />} />
												</Route>
												<Route path="application/*">
													<Route index element={<Navigate to="list" replace />} />
													<Route path="list" element={<ProgramApplicationList />} />
													<Route path=":applicationId" element={<ProgramPairingDetails />} />
												</Route>
												<Route path="membership/*">
													<Route index element={<Navigate to="list" replace />} />
													<Route path="list" element={<ProgramMembershipList />} />
													<Route path=":membershipId" element={<ProgramMembership />} />
												</Route>
												<Route path="communication/*">
													<Route index element={<Navigate to="list" replace />} />
													<Route path="list" element={<ProgramPostList />} />
													<Route path="add" element={<ProgramPostAdd />} />
													<Route path="edit/:id" element={<ProgramPostEdit />} />
													<Route path="mentibot" element={<ProgramPostMentibotInfo />} />
												</Route>
												<Route path="statistics/*" element={<ProgramStatisticsNew />} />
												<Route
													path="feedback/edit-questions"
													element={<ProgramFeedbackEditQuestions />}
												/>
												<Route path="feedback" element={<ProgramFeedback />} />
												<Route path="edit-questions" element={<EditApplicationQuestions />} />
												<Route path="contract" element={<EditContract />} />
												<Route
													path="knowledge-base/*"
													element={<ProgramKnowledgeBaseContainer />}
												/>
												<Route path="landing-page" element={<ProgramLandingPageEditor />} />
												<Route path="timeline">
													<Route index element={<Navigate to="list" replace />} />
													<Route path="list" element={<ProgramTimelineList />} />
													<Route path="add" element={<ProgramTimelineEventAdd />} />
													<Route
														path="details/:eventId"
														element={<ProgramTimelineEventDetails />}
													/>
													<Route
														path="edit/:eventId"
														element={<ProgramTimelineEventEdit />}
													/>
												</Route>
											</Route>
										</Route>
										<Route
											path="coordinator/:organizationName/:programName/*"
											element={
												<ScopeChatContainer context="coordinator">
													<CoordinatorPanel />
												</ScopeChatContainer>
											}
										>
											<Route index element={<Navigate to="dashboard" replace />} />
											<Route path="dashboard" element={<CoordinatorProgramDashboard />} />
											<Route path="pair/*">
												<Route index element={<Navigate to="list" replace />} />
												<Route path="list" element={<CoordinatorPairList />} />
												<Route path=":pairId" element={<CoordinatorPairDetails />} />
											</Route>
											<Route path="application/*">
												<Route index element={<Navigate to="list" replace />} />
												<Route
													path=":applicationId"
													element={<CoordinatorApplicationDetails />}
												/>
											</Route>
											<Route
												path="knowledge-base/*"
												element={<CoordinatorKnowledgeBaseContainer />}
											/>
										</Route>

										<Route
											element={
												<ScopeChatContainer context="panel">
													<Outlet />
												</ScopeChatContainer>
											}
										>
											<Route
												path=":organizationName/:programName/*"
												element={<ProgramLandingPage />}
											/>
											<Route path=":organizationName" element={<OrganizationLandingPage />} />
										</Route>
									</Route>
									<Route path="/final-survey/:uuid" element={<FinalSurveyAccess />} />
									<Route path="*" element={<PageMissing />} />
								</Routes>
							</Suspense>
							<ReactQueryDevtools initialIsOpen={false} />
						</AuthContextProvider>
					</InstallPromptProvider>
				</GlobalContextProvider>
			</QueryClientProvider>
		</>
	);
};

export default App;
